import type { Worksheet } from 'exceljs';
import { ExcelTable } from './table';

const DEFAULT_EMPTY_LINES_COUNT_BETWEEN_TABLES = 1;

interface CellEntry {
  row: number;  // 0-based
  col: number;  // 0-based
  value: any;
  numFmt?: string;
}

interface MergeEntry {
  startRow: number;  // 0-based
  startCol: number;  // 0-based
  endRow: number;    // 0-based
  endCol: number;    // 0-based
}

export class ExcelWorkSheet {
  private _cells: CellEntry[] = [];
  private _merges: MergeEntry[] = [];
  private _columnWidths: number[] = [];
  private nextEmptyRow = 0;

  constructor(private _name: string, table?: ExcelTable) {
    if (table) {
      this.appendTable(table, { skipLines: 0 });
    }
  }

  public get name(): string {
    return this._name;
  }

  /** Exposes computed column widths; useful for testing and diagnostics. */
  public get columnWidths(): number[] {
    return [...this._columnWidths];
  }

  /**
   * Writes all accumulated cell data, merges, and column widths onto
   * an ExcelJS Worksheet instance (created by the owning Workbook).
   */
  public applyToWorksheet(ws: Worksheet): void {
    this._cells.forEach(({ row, col, value, numFmt }) => {
      const cell = ws.getCell(row + 1, col + 1);
      cell.value = value;
      if (numFmt) {
        cell.numFmt = numFmt;
      }
    });

    this._merges.forEach(({ startRow, startCol, endRow, endCol }) => {
      ws.mergeCells(startRow + 1, startCol + 1, endRow + 1, endCol + 1);
    });

    this._columnWidths.forEach((width, colI) => {
      if (width > 0) {
        ws.getColumn(colI + 1).width = width;
      }
    });
  }

  public appendTable(
    table: ExcelTable,
    options: { skipLines?: number } = {}
  ): void {
    const skipLines =
      typeof options.skipLines === 'number'
        ? options.skipLines
        : DEFAULT_EMPTY_LINES_COUNT_BETWEEN_TABLES;
    const originRow = this.nextEmptyRow + skipLines;

    const values = table.getValues();
    const formatting = table.getFormatting();
    const colSpan = table.getColSpan();

    // Store cell entries with value and optional number format
    for (let rowI = 0; rowI < table.rowsCount; rowI++) {
      for (let colI = 0; colI < table.colsCount; colI++) {
        const entry: CellEntry = {
          row: originRow + rowI,
          col: colI,
          value: values[rowI][colI]
        };
        const fmt = formatting[rowI][colI];
        if (fmt !== null && fmt !== undefined) {
          entry.numFmt = fmt;
        }
        this._cells.push(entry);
      }
    }

    // Store merge entries for cells with colspan > 1
    for (let rowI = 0; rowI < table.rowsCount; rowI++) {
      for (let colI = 0; colI < table.colsCount; colI++) {
        const span = colSpan[rowI][colI];
        if (!span || span <= 1) continue;
        this._merges.push({
          startRow: originRow + rowI,
          startCol: colI,
          endRow: originRow + rowI,
          endCol: colI + span - 1
        });
      }
    }

    this.updateColumnWidths(table, values);

    this.nextEmptyRow = originRow + table.rowsCount;
  }

  private updateColumnWidths(table: ExcelTable, values: any[][]): void {
    const filledCount = Math.max(this._columnWidths.length, table.colsCount);
    const newWidths = new Array(filledCount).fill(0);

    for (let colI = 0; colI < filledCount; colI++) {
      newWidths[colI] = this._columnWidths[colI] || 0;
    }

    for (let rowI = 0; rowI < table.rowsCount; rowI++) {
      for (let colI = 0; colI < table.colsCount; colI++) {
        const shouldBeVisible =
          table.exportConfig.shouldBeFullyVisible ||
          (table.exportConfig.columnsConfig &&
            table.exportConfig.columnsConfig[colI]?.shouldBeFullyVisible);

        if (shouldBeVisible) {
          const value = values[rowI][colI];
          if (value !== null && value !== undefined && value !== '') {
            const len = String(value).length;
            if (len > newWidths[colI]) {
              newWidths[colI] = len;
            }
          }
        }
      }
    }

    this._columnWidths = newWidths;
  }
}
